import React from "react";
import type {
  ComponentPropsWithRef,
  CSSProperties,
  ReactNode,
  TdHTMLAttributes,
} from 'react';
import { blockWidthAlignMargins } from '../../domain/block-width';
import {
  textBlockShellTableStyle,
  textBlockTypographyStyle,
} from '../../domain/blocks/text-presentation';
import type { Align , TextBlock } from '../../domain/types';
import type { TextTheme } from '../../domain/text-theme';
import { padStyle, presentationTableProps } from '../table';

type TextBlockShellProps = {
  block: TextBlock;
  textTheme: TextTheme;
  widthPx?: number | null;
  previewWidthPx?: number | null;
  children?: ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  outerCellStyle?: CSSProperties;
  outerCellProps?: TdHTMLAttributes<HTMLTableCellElement>;
  contentCellProps?: ComponentPropsWithRef<'td'> & {
    'data-text-block'?: string;
    'data-text-editing'?: string;
  };
  trailing?: ReactNode;
  contentClassName?: string;
  autoWidth?: boolean;
};

function editorWrapperStyle(
  align: Align,
  autoWidth: boolean | undefined,
  style?: CSSProperties,
): CSSProperties | undefined {
  if (style) {
    return style;
  }
  if (!autoWidth) {
    return undefined;
  }
  return blockWidthAlignMargins(align);
}

function wrapEditorContent(
  align: Align,
  content: ReactNode,
  className: string | undefined,
  trailing?: ReactNode,
  autoWidth?: boolean,
  style?: CSSProperties,
) {
  const wrapperStyle = editorWrapperStyle(align, autoWidth, style);
  if (!className) {
    return (
      <>
        {content}
        {trailing}
      </>
    );
  }
  return (
    <div
      className={className}
      data-auto-width={autoWidth ? 'true' : undefined}
      style={wrapperStyle}>
      {content}
      {trailing}
    </div>
  );
}

export function TextBlockShell({
  block,
  textTheme,
  widthPx,
  previewWidthPx,
  children,
  dangerouslySetInnerHTML,
  outerCellStyle,
  outerCellProps,
  contentCellProps,
  trailing,
  contentClassName,
  autoWidth,
}: TextBlockShellProps) {
  const effectiveWidth = widthPx !== undefined ? widthPx : block.widthPx;
  const isEditor = children != null && dangerouslySetInnerHTML == null;

  if (effectiveWidth == null) {
    return (
      <table {...presentationTableProps()}>
        <tbody>
          <tr>
            <td
              style={{ ...padStyle(block.padding), ...outerCellStyle }}
              {...outerCellProps}>
              {isEditor ? (
                <>
                  <table
                    {...presentationTableProps()}
                    style={{
                      ...presentationTableProps().style,
                      width: '100%',
                    }}>
                    <tbody>
                      <tr>
                        <td
                          style={textBlockTypographyStyle(block, textTheme)}
                          {...contentCellProps}>
                          {wrapEditorContent(
                            block.align,
                            children,
                            contentClassName,
                            undefined,
                            autoWidth && previewWidthPx == null,
                            previewWidthPx != null
                              ? textBlockShellTableStyle(block, previewWidthPx)
                              : undefined,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {trailing}
                </>
              ) : previewWidthPx != null ? (
                <>
                  <table
                    {...presentationTableProps()}
                    width="100%"
                    style={{
                      ...presentationTableProps().style,
                      ...textBlockShellTableStyle(block, previewWidthPx),
                    }}>
                    <tbody>
                      <tr>
                        <td
                          style={textBlockTypographyStyle(block, textTheme)}
                          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
                          {...contentCellProps}
                        />
                      </tr>
                    </tbody>
                  </table>
                  {trailing}
                </>
              ) : (
                <>
                  <table
                    {...presentationTableProps()}
                    style={{
                      ...presentationTableProps().style,
                      width: '100%',
                    }}>
                    <tbody>
                      <tr>
                        <td
                          style={textBlockTypographyStyle(block, textTheme)}
                          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
                          {...contentCellProps}
                        />
                      </tr>
                    </tbody>
                  </table>
                  {trailing}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  if (isEditor) {
    const renderedWidth = previewWidthPx ?? effectiveWidth;
    return (
      <table {...presentationTableProps()}>
        <tbody>
          <tr>
            <td
              style={{ ...padStyle(block.padding), ...outerCellStyle }}
              {...outerCellProps}>
              <table
                {...presentationTableProps()}
                width="100%"
                style={{
                  ...presentationTableProps().style,
                  ...textBlockShellTableStyle(block, renderedWidth),
                }}>
                <tbody>
                  <tr>
                    <td
                      style={textBlockTypographyStyle(block, textTheme)}
                      {...contentCellProps}>
                      {children}
                    </td>
                  </tr>
                </tbody>
              </table>
              {trailing}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table {...presentationTableProps()}>
      <tbody>
        <tr>
          <td
            style={{ ...padStyle(block.padding), ...outerCellStyle }}
            {...outerCellProps}>
            <table
              {...presentationTableProps()}
              width="100%"
              style={{
                ...presentationTableProps().style,
                ...textBlockShellTableStyle(
                  block,
                  previewWidthPx ?? effectiveWidth,
                ),
              }}>
              <tbody>
                <tr>
                  <td
                    style={textBlockTypographyStyle(block, textTheme)}
                    dangerouslySetInnerHTML={dangerouslySetInnerHTML}
                    {...contentCellProps}
                  />
                </tr>
              </tbody>
            </table>
            {trailing}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
